import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, Upload, FileText, Plus, Trash2, Edit, 
  Calendar, Clock, Utensils, ChevronRight, CheckCircle,
  AlertCircle, Image, X, RefreshCw, Loader2
} from "lucide-react";
import { toast } from "sonner";

const MyMealPlans = () => {
  const { user, token } = useAuth();
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const fileInputRef = useRef(null);

  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    plan_duration: 1,
    selectedDay: 0,
    days: [{
      day_number: 1,
      day_label: "Day 1",
      meals: {
        breakfast: { foods: "", notes: "" },
        morning_snack: { foods: "", notes: "" },
        lunch: { foods: "", notes: "" },
        afternoon_snack: { foods: "", notes: "" },
        dinner: { foods: "", notes: "" },
        evening_snack: { foods: "", notes: "" }
      }
    }]
  });

  const [uploadForm, setUploadForm] = useState({
    name: "",
    file: null,
    filePreview: null
  });

  useEffect(() => {
    fetchMealPlans();
  }, [token]);

  const fetchMealPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/meal-plans/my-plans`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setMealPlans(data.plans || []);
      }
    } catch (error) {
      console.error("Fetch meal plans error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      // Count meals per day from first day
      const firstDayMeals = createForm.days[0]?.meals || {};
      const mealsWithContent = Object.values(firstDayMeals).filter(m => m?.foods?.trim()).length;
      
      const payload = {
        name: createForm.name,
        description: createForm.description,
        plan_duration: createForm.days.length,
        meals_per_day: mealsWithContent || 3,
        days: createForm.days,
        // For backwards compatibility
        daily_breakdown: createForm.days[0]?.meals || {}
      };

      const response = await fetch(`${API}/meal-plans/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Meal plan created!");
        setShowCreateModal(false);
        resetCreateForm();
        fetchMealPlans();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create meal plan");
      }
    } catch (error) {
      console.error("Create meal plan error:", error);
      toast.error("Failed to create meal plan");
    } finally {
      setCreating(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const validTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a PDF or image file");
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }

      setUploadForm({
        ...uploadForm,
        file,
        filePreview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null
      });
    }
  };

  const handleUploadPlan = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);

    try {
      const file = uploadForm.file;
      let processedBase64 = null;
      let fileType = file.type;

      // For images, convert to a supported format (png/jpeg)
      if (file.type.startsWith("image/")) {
        // Convert image to canvas and then to JPEG for compatibility
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = objectUrl;
        });
        
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        
        // Convert to JPEG base64
        processedBase64 = canvas.toDataURL("image/jpeg", 0.9).split(",")[1];
        fileType = "image/jpeg";
        
        URL.revokeObjectURL(objectUrl);
      } else {
        // For non-images (PDFs), read as-is
        processedBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => resolve(event.target.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      const response = await fetch(`${API}/meal-plans/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          name: uploadForm.name || "My Meal Plan",
          file_base64: processedBase64,
          file_type: fileType,
          file_name: uploadForm.file.name
        }),
      });

      if (response.ok) {
        toast.success("Meal plan uploaded and processed!");
        setShowUploadModal(false);
        resetUploadForm();
        fetchMealPlans();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to upload meal plan");
      }
    } catch (error) {
      console.error("Upload meal plan error:", error);
      toast.error("Failed to upload meal plan");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm("Are you sure you want to delete this meal plan?")) return;

    try {
      const response = await fetch(`${API}/meal-plans/${planId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        toast.success("Meal plan deleted");
        fetchMealPlans();
      } else {
        toast.error("Failed to delete meal plan");
      }
    } catch (error) {
      console.error("Delete meal plan error:", error);
      toast.error("Failed to delete meal plan");
    }
  };

  const handleReanalyze = async (planId) => {
    setReanalyzing(planId);
    try {
      const response = await fetch(`${API}/meal-plans/${planId}/reanalyze`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Re-analyzed! ${data.days_extracted} days extracted.`);
        fetchMealPlans();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Re-analysis failed");
      }
    } catch (error) {
      console.error("Reanalyze error:", error);
      toast.error("Failed to re-analyze meal plan");
    } finally {
      setReanalyzing(null);
    }
  };


  const resetCreateForm = () => {
    setCreateForm({
      name: "",
      description: "",
      plan_duration: 1,
      selectedDay: 0,
      days: [{
        day_number: 1,
        day_label: "Day 1",
        meals: {
          breakfast: { foods: "", notes: "" },
          morning_snack: { foods: "", notes: "" },
          lunch: { foods: "", notes: "" },
          afternoon_snack: { foods: "", notes: "" },
          dinner: { foods: "", notes: "" },
          evening_snack: { foods: "", notes: "" }
        }
      }]
    });
  };

  const addDayToCreate = () => {
    const newDayNum = createForm.days.length + 1;
    setCreateForm(prev => ({
      ...prev,
      days: [...prev.days, {
        day_number: newDayNum,
        day_label: `Day ${newDayNum}`,
        meals: {
          breakfast: { foods: "", notes: "" },
          morning_snack: { foods: "", notes: "" },
          lunch: { foods: "", notes: "" },
          afternoon_snack: { foods: "", notes: "" },
          dinner: { foods: "", notes: "" },
          evening_snack: { foods: "", notes: "" }
        }
      }]
    }));
  };

  const removeDayFromCreate = (dayIndex) => {
    if (createForm.days.length <= 1) return;
    setCreateForm(prev => ({
      ...prev,
      days: prev.days.filter((_, i) => i !== dayIndex).map((d, i) => ({
        ...d,
        day_number: i + 1,
        day_label: `Day ${i + 1}`
      })),
      selectedDay: Math.min(prev.selectedDay, prev.days.length - 2)
    }));
  };

  const updateCreateMeal = (mealKey, value) => {
    setCreateForm(prev => {
      const newDays = [...prev.days];
      newDays[prev.selectedDay].meals[mealKey] = { 
        ...newDays[prev.selectedDay].meals[mealKey], 
        foods: value 
      };
      return { ...prev, days: newDays };
    });
  };

  const resetUploadForm = () => {
    setUploadForm({
      name: "",
      file: null,
      filePreview: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20" />
          <p className="text-muted-foreground">Loading your meal plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-secondary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-secondary">My Meal Plans</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            onClick={() => setShowCreateModal(true)}
            className="rounded-2xl bg-primary text-white hover:bg-primary/90 py-6"
            data-testid="create-plan-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Plan
          </Button>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            className="rounded-2xl border-2 border-primary text-primary hover:bg-primary/5 py-6"
            data-testid="upload-plan-btn"
          >
            <Upload className="w-5 h-5 mr-2" />
            Upload Plan
          </Button>
        </div>

        {/* Plans List */}
        {mealPlans.length === 0 ? (
          <Card className="bg-white rounded-3xl border border-orange-100/50 p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <Utensils className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary">No Meal Plans Yet</h3>
              <p className="text-muted-foreground max-w-md">
                Create your own meal plan or upload one from your dietitian. Your plans will help you track what to eat each day.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {mealPlans.map((plan) => (
              <Card
                key={plan.plan_id}
                className="bg-white rounded-3xl border border-orange-100/50 hover:shadow-soft-lift transition-all"
                data-testid={`plan-card-${plan.plan_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-secondary">{plan.name}</h3>
                        {plan.source === "upload" && (
                          <Badge variant="outline" className="text-xs">Uploaded</Badge>
                        )}
                        {plan.is_active && (
                          <Badge className="bg-green-100 text-green-700 border-0">Active</Badge>
                        )}
                        {plan.extraction_status === "failed" && (
                          <Badge className="bg-red-100 text-red-700 border-0 text-xs">Extraction Failed</Badge>
                        )}
                      </div>
                      
                      {plan.description && (
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{plan.description}</p>
                      )}
                      
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Created {formatDate(plan.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Utensils className="w-3 h-3" />
                          {plan.meals_per_day} meals/day
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 sm:gap-2">
                      {plan.source === "upload" && (plan.extraction_status === "failed" || (plan.days && plan.days.length > 0 && plan.days[0]?.meals?.breakfast?.foods?.includes("Could not extract"))) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReanalyze(plan.plan_id)}
                          disabled={reanalyzing === plan.plan_id}
                          className="rounded-full text-blue-600 border-blue-200 hover:bg-blue-50 text-xs"
                          data-testid={`reanalyze-${plan.plan_id}`}
                        >
                          {reanalyzing === plan.plan_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                          ) : (
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                          )}
                          Re-analyze
                        </Button>
                      )}
                      <Link to={`/my-meal-plans/${plan.plan_id}`}>
                        <Button variant="ghost" size="sm" className="rounded-full">
                          View
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePlan(plan.plan_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Plan Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create Meal Plan</DialogTitle>
            <DialogDescription>
              Build your own meal plan by specifying what to eat for each meal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePlan} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="plan-name">Plan Name</Label>
              <Input
                id="plan-name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="e.g., My Weekly Plan"
                className="rounded-xl"
                required
                data-testid="plan-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan-description">Description (Optional)</Label>
              <Textarea
                id="plan-description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Brief description of your meal plan..."
                className="rounded-xl"
                data-testid="plan-description-input"
              />
            </div>

            {/* Day Navigation */}
            <div className="space-y-2">
              <Label>Days in Plan</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {createForm.days.map((day, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant={createForm.selectedDay === index ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCreateForm({ ...createForm, selectedDay: index })}
                    className={`rounded-full ${createForm.selectedDay === index ? "bg-primary" : ""}`}
                  >
                    D{index + 1}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addDayToCreate}
                  className="rounded-full"
                >
                  + Add Day
                </Button>
              </div>
              {createForm.days.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Editing Day {createForm.selectedDay + 1} of {createForm.days.length}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeDayFromCreate(createForm.selectedDay)}
                    className="ml-2 text-red-500 h-6 px-2"
                  >
                    Remove this day
                  </Button>
                </p>
              )}
            </div>

            <div className="space-y-4 border-t pt-4">
              <Label className="text-base">Day {createForm.selectedDay + 1} Meals</Label>
              
              {[
                { key: "breakfast", label: "Breakfast" },
                { key: "morning_snack", label: "Morning Snack (optional)" },
                { key: "lunch", label: "Lunch" },
                { key: "afternoon_snack", label: "Afternoon Snack (optional)" },
                { key: "dinner", label: "Dinner" },
                { key: "evening_snack", label: "Evening Snack (optional)" }
              ].map((meal) => (
                <div key={meal.key} className="space-y-1">
                  <Label className="text-sm">{meal.label}</Label>
                  <Textarea
                    value={createForm.days[createForm.selectedDay]?.meals[meal.key]?.foods || ""}
                    onChange={(e) => updateCreateMeal(meal.key, e.target.value)}
                    placeholder={`What to eat for ${meal.label.toLowerCase().replace(' (optional)', '')}...`}
                    className="rounded-xl min-h-[50px]"
                    data-testid={`${meal.key}-foods-input`}
                  />
                </div>
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="rounded-full bg-primary text-white hover:bg-primary/90"
                data-testid="submit-create-plan"
              >
                {creating ? "Creating..." : "Create Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Plan Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Upload Meal Plan</DialogTitle>
            <DialogDescription>
              Upload a PDF or image of your meal plan. We'll extract the information automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadPlan} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="upload-name">Plan Name</Label>
              <Input
                id="upload-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="e.g., Dietitian's Meal Plan"
                className="rounded-xl"
                data-testid="upload-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Upload File</Label>
              <div 
                className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-colors ${
                  uploadForm.file ? "border-primary bg-primary/5" : "border-gray-200 hover:border-primary"
                }`}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadForm.filePreview ? (
                  <div className="relative">
                    <img 
                      src={uploadForm.filePreview} 
                      alt="Preview" 
                      className="max-h-40 mx-auto rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        resetUploadForm();
                      }}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : uploadForm.file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-8 h-8 text-primary" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-secondary">{uploadForm.file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        resetUploadForm();
                      }}
                      className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload your meal plan
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Supports PDF, Word, and images (Max 10MB)
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            <div className="bg-orange-50 p-3 rounded-xl">
              <p className="text-xs text-muted-foreground">
                <AlertCircle className="inline w-3 h-3 mr-1" />
                We'll use AI to extract meal information from your file. Review the extracted data before saving.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading || !uploadForm.file}
                className="rounded-full bg-primary text-white hover:bg-primary/90"
                data-testid="submit-upload-plan"
              >
                {uploading ? "Processing..." : "Upload & Process"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyMealPlans;
